<template>
  <div class="import-reports-index-view">
    <h1>{{$t('titles.index')}}</h1>

    <h3>Neuen Import starten</h3>
    <confirmation-button
      :text="$t('actions.enqueueBrodosFullImport')"
      @confirm="enqueueBrodosFullImport()"
    />

    <confirmation-button
      :text="$t('actions.enqueueBrodosFullImportWithoutAttachments')"
      @confirm="enqueueBrodosFullImportWithoutAttachments()"
    />

    <confirmation-button
      :text="$t('actions.enqueueBrodosDynamicDataImport')"
      @confirm="enqueueBrodosDynamicDataImport()"
    />

    <confirmation-button
      :text="$t('actions.enqueueBrodosBundleImport')"
      @confirm="enqueueBrodosBundleImport()"
    />

    <confirmation-button
      :text="$t('actions.enqueuePxsdbFullImport')"
      @confirm="enqueuePxsdbFullImport()"
    />


    <hr/>

    <h3>Bisherige Importe</h3>
    
    <select name="importer" v-model="importer" @change="changeImporterFilter">
      <option value="">Alle</option>
      <option value="brodos,brodos_articles,pxsdb_articles">Artikel - Alle </option>
      <option value="pxsdb_articles">Artikel - Pxsdb</option>
      <option value="brodos,brodos_articles">Artikel - Brodos</option>
      <option value="brodos_bundles">Bundles</option>
      <option value="brodos,brodos_categories">Kategorien</option>
      <option value="brodos,brodos_dynamic_data,telekom_product_availabilities">Verfügbarkeiten</option>
      <option value="brodos">Alte Vollimporte</option>
    </select>
    <data-grid
      :columns="gridColumns"
      :editable="false"
      :is-loading="isLoading"
      :page-info="importReports.pagination"
      :removable="false"
      :sortable="['importer', 'scope', 'status', 'insertedAt']"
      :sort-info="importReports.sort"
      :rows="importReports.entries"
      class="uk-margin-medium"
      v-if="gridLoaded"
      @sort="sortBy"

    >
      <template scope="importerProps" slot="importer">
        <router-link
          :to="{
          name: 'import-report',
          params: { id: importerProps.row.id }
        }"
        >{{$t(`importers.${importerProps.value}`)}}</router-link>
      </template>
      <template scope="statusProps" slot="status">
        <import-status
          :errors="statusProps.row.errorsCount"
          :value="statusProps.value"
        />
      </template>
      <template scope="scopeProps" slot="scope">
        <span v-if="scopeProps.value">
         {{scopeProps.value }}
         <br/>
         {{scopeProps.row.fileName}}
        </span>
        <text-placeholder v-else />
      </template>
      <template scope="wallclock" slot="insertedAt">
         <span v-if="wallclock.row.startedAt">
           ↦ {{wallclock.row.startedAt | niceTime}}
         </span>
         <span v-if="wallclock.row.finishedAt">
           <br/>
            ⇥ {{wallclock.row.finishedAt | niceTime}}
         </span>
      </template>
    </data-grid>

    <div
      class="loader-container uk-container uk-margin-medium-top uk-text-center"
      v-else
    >
      <loader />
    </div>
  </div>
</template>

<script>
import ConfirmationButton from '@/components/ConfirmationButton';
import enqueueBrodosDynamicDataImportMutation
  from '@/api/mutations/enqueue-brodos-dynamic-data-import.graphql';
import enqueueBrodosImportMutation
  from '@/api/mutations/enqueue-brodos-import.graphql';
import enqueueBrodosBundleImportMutation
  from '@/api/mutations/enqueue-brodos-bundle-import.graphql';
import enqueuePxsdbFullImportMutation
  from '@/api/mutations/enqueue-pxsdb-full-import.graphql';
import ImportStatus from '@/components/ImportStatus';
import IndexView from '@/mixins/IndexView';
import importReportsQuery from '@/api/queries/import-reports.graphql';
import TextPlaceholder from '@/components/TextPlaceholder';
import Toast from '@/helpers/Toast';

export default {
  name: 'import-reports-index-view',
  computed: {
    queryVariables() {
      const importerFilter = this.$route.query.importer
        ? this.$route.query.importer.split(',')
        : undefined;
      return {
        filter: { importer: importerFilter },
        pagination: this.pageInfo,
        sort: this.sortInfo,
        search: this.searchQuery,
      };
    },
  },
  mixins: [
    IndexView('importReports', {
      gridFields: ['importer', 'status', 'scope', 'insertedAt'],
      listQuery: importReportsQuery,
      nameField: 'name',
      pollInterval: 60000,
    }),
  ],
  data() {
    return {
      importer: this.$route.query.importer || '',
    };
  },
  components: {
    ConfirmationButton,
    ImportStatus,
    TextPlaceholder,
  },
  methods: {
    enqueueBrodosFullImport() {
      return this.enqueueImport(
        'enqueueBrodosImport',
        enqueueBrodosImportMutation,
        { withAttachments: true, force: true },
      );
    },
    enqueueBrodosFullImportWithoutAttachments() {
      return this.enqueueImport(
        'enqueueBrodosImport',
        enqueueBrodosImportMutation,
        { withAttachments: false, force: true },
      );
    },
    async enqueueBrodosDynamicDataImport() {
      return this.enqueueImport(
        'enqueueBrodosDynamicDataImport',
        enqueueBrodosDynamicDataImportMutation,
      );
    },
    async enqueueBrodosBundleImport() {
      return this.enqueueImport(
        'enqueueBrodosBundleImport',
        enqueueBrodosBundleImportMutation,
      );
    },
    async enqueuePxsdbFullImport() {
      return this.enqueueImport(
        'enqueuePxsdbFullImport',
        enqueuePxsdbFullImportMutation,
      );
    },
    async enqueueImport(key, mutation, variables = {}) {
      const result = await this.$apollo.mutate({ mutation, variables });

      if (result.data[key].queued) {
        await this.refetchReports();
        Toast.success(this.$t('toasts.queued'));
      } else {
        Toast.warn(this.$t('toasts.notQueued'));
      }
    },
    refetchReports() {
      return this.$apollo.queries.importReports.refetch();
    },
    changeImporterFilter() {
      this.$router.push({
        name: 'import-reports',
        query: { importer: this.importer },
      });
    },
  },

  i18n: {
    messages: {
      de: {
        actions: {
          enqueueBrodosFullImport: 'Brodos: Vollständiger Import',
          enqueueBrodosFullImportWithoutAttachments:
            'Brodos: Import ohne Dateidownload',
          enqueueBrodosDynamicDataImport: 'Brodos: Bewegungsdaten',
          enqueueBrodosBundleImport: 'Brodos: Bundles',
          enqueuePxsdbFullImport: 'PXSDB: Artikel und Bewegungsdaten',
        },
        hints: {
          empty: 'Keine Importe gefunden.',
        },
        titles: {
          index: 'Importe',
        },
        toasts: {
          queued: 'Import wurde zur Warteschlange hinzugefügt',
          notQueued: 'Import ist bereits in der Warteschlange',
        },
      },
    },
  },
};
</script>



// WEBPACK FOOTER //
// IndexView.vue?7012cd08